import type { IconButtonProps } from '@mui/material/IconButton';

import { useState, useCallback, useEffect, useRef } from 'react';

import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate } from 'react-router';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import { fToNow } from 'utils/format-time';
import { Iconify } from 'components/iconify';
import ReactIntlWrapper from 'components/i18n/ReactIntlWrapper';
import { categorizeByDate } from 'common/utils';
import { Notification } from 'axiosApi/models';
import { getNotificationLocale, useResolvedParams } from 'components/notifications/utils';
import { PriorityIcon } from 'components/common/PriorityIcon';
import { CircleTwoTone, Notifications as MUINotification, Visibility as VisibilityIcon } from '@mui/icons-material';
import SimpleBar from 'simplebar-react';
import {
  useMarkAllAsReadMutation,
  useMarkAsReadMutation,
  useNotificationsStorage,
} from 'store/registry/useNotificationsStorage';
import { DEFAULT_PAGINATION, Pagination } from 'hooks/useSearch';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';

// ----------------------------------------------------------------------

export type NotificationsPopoverProps = IconButtonProps & {
  data?: Notification[];
};

export function NotificationsPopover() {
  const toast = useToastMessageQueue();
  const navigate = useNavigate();
  const api: Api = useApi();

  const [openPopover, setOpenPopover] = useState<HTMLButtonElement | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState({});
  const [paginationInfo, setPaginationInfo] = useState<Pagination>(DEFAULT_PAGINATION);
  const lastCallRef = useRef(0);
  const [maxPageNumReached, setMaxPageReached] = useState(false);
  const [isMarking, setIsMarking] = useState(false);

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);

  const [todayNotifications, setTodayNotifications] = useState<Notification[]>([]);
  const [yesterdayNotifications, setYesterdayNotifications] = useState<Notification[]>([]);
  const [olderNotifications, setOlderNotifications] = useState<Notification[]>([]);

  const { data, isFetching, isRefetching, isLoading } = useNotificationsStorage(
    paginationInfo.pageNum,
    paginationInfo.pageSize
  );
  const { mutateAsync: markAsRead } = useMarkAsReadMutation();
  const { mutateAsync: markAllAsRead } = useMarkAllAsReadMutation();

  useEffect(() => {
    if (!data) return;

    const queryResult = data.queryResult || [];
    const totalRecords = data.totalRecords || 0;

    if (Array.isArray(queryResult) && queryResult.length < paginationInfo.pageSize) {
      setMaxPageReached(true);
      return;
    }
    setUnreadNotificationsCount(totalRecords);
    if (paginationInfo.pageNum === DEFAULT_PAGINATION.pageNum) {
      setNotifications(queryResult);
    } else {
      setNotifications((prev) => {
        const existingTimestamps = new Set(prev.map((n) => n.timeStamp));
        const newNotifications = queryResult.filter((n) => !existingTimestamps.has(n.timeStamp));
        return [...prev, ...newNotifications];
      });
    }
  }, [data, paginationInfo.pageNum]);

  useEffect(() => {
    setLoading(isLoading || isRefetching || isFetching);
  }, [isLoading, isFetching, isRefetching]);

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      return true;
    } catch (error) {
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
      return false;
    }
  };

  const handleItemAsRead = async (notification: Notification) => {
    if (Number(notification.read) === 1) return;
    if (isMarking) return;
    setIsMarking(true);
    try {
      await markAsRead(notification.timeStamp);
      
      // Update local state to reflect the read status
      const updateNotificationRead = (notifications: Notification[]) =>
        notifications.map((n) =>
          n.timeStamp === notification.timeStamp ? { ...n, read: '1' } : n
        );
      
      setNotifications((prev) => updateNotificationRead(prev));
      setTodayNotifications((prev) => updateNotificationRead(prev));
      setYesterdayNotifications((prev) => updateNotificationRead(prev));
      setOlderNotifications((prev) => updateNotificationRead(prev));
      
      return true;
    } catch (error) {
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
      return false;
    }finally {
      setIsMarking(false);
    }
  };

  const handleOpenPopover = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setOpenPopover(event.currentTarget);
    setPaginationInfo(DEFAULT_PAGINATION);
    setMaxPageReached(false);
  }, []);

  const handleClosePopover = useCallback(() => {
    setOpenPopover(null);
  }, []);

  useEffect(() => {
    if (notifications.length > 0) {
      const { today, yesterday, older } = categorizeByDate(notifications);

      if (paginationInfo.pageNum === DEFAULT_PAGINATION.pageNum) {
        setTodayNotifications(today);
        setYesterdayNotifications(yesterday);
        setOlderNotifications(older);
      } else {
        setTodayNotifications((prev) => {
          const existingTimestamps = new Set(prev.map((n) => n.timeStamp));
          const newToday = today.filter((n) => !existingTimestamps.has(n.timeStamp));
          return [...prev, ...newToday];
        });

        setYesterdayNotifications((prev) => {
          const existingTimestamps = new Set(prev.map((n) => n.timeStamp));
          const newYesterday = yesterday.filter((n) => !existingTimestamps.has(n.timeStamp));
          return [...prev, ...newYesterday];
        });

        setOlderNotifications((prev) => {
          const existingTimestamps = new Set(prev.map((n) => n.timeStamp));
          const newOlder = older.filter((n) => !existingTimestamps.has(n.timeStamp));
          return [...prev, ...newOlder];
        });
      }
    }
  }, [notifications]);

  const handleScrollChange = (event: React.UIEvent<HTMLDivElement>) => {
    const now = Date.now();
    if (now - lastCallRef.current < 1500) return;
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (!maxPageNumReached && scrollTop + clientHeight >= scrollHeight - 5) {
      setPaginationInfo((prev) => ({ ...prev, pageNum: prev.pageNum + 1 }));
      lastCallRef.current = now;
    }
  };

  return (
    <>
      <IconButton color={openPopover ? 'primary' : 'default'} onClick={handleOpenPopover}>
        <Badge badgeContent={unreadNotificationsCount} color="error">
          <MUINotification />
        </Badge>
      </IconButton>

      <Popover
        open={!!openPopover}
        anchorEl={openPopover}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableScrollLock={true}
        slotProps={{
          paper: {
            sx: {
              width: 360,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        <Box display="flex" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1.5, flexShrink: 0 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">{ReactIntlWrapper.get('notifications')}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {ReactIntlWrapper.get('unreadNotifications_counter', {
                count: unreadNotificationsCount,
              })}
            </Typography>
          </Box>

          {unreadNotificationsCount > 0 && (
            <Tooltip title=" Mark all as read">
              <IconButton color="primary" onClick={handleMarkAllAsRead}>
                <Iconify icon="solar:check-read-outline" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <SimpleBar
          style={{
            minHeight: 240,
            maxHeight: 360,
            flexGrow: 1,
          }}
          scrollableNodeProps={{
            onScroll: handleScrollChange,
          }}
        >
          <List
            disablePadding
            subheader={
              todayNotifications.length > 0 && (
                <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
                  {ReactIntlWrapper.get('genericCalendar.customToolbar.button.today')}
                </ListSubheader>
              )
            }
          >
            {todayNotifications?.map((notification) => (
              <NotificationItem
                key={notification.timeStamp}
                notification={notification}
                markAsRead={() => handleItemAsRead(notification)}
                api={api}
              />
            ))}
          </List>

          <List
            disablePadding
            subheader={
              yesterdayNotifications.length > 0 && (
                <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
                  {ReactIntlWrapper.get('yesterday')}
                </ListSubheader>
              )
            }
          >
            {yesterdayNotifications?.map((notification) => (
              <NotificationItem
                key={notification.timeStamp}
                notification={notification}
                markAsRead={() => handleItemAsRead(notification)}
                api={api}
              />
            ))}
          </List>
          <List
            disablePadding
            subheader={
              olderNotifications.length > 0 && (
                <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
                  {ReactIntlWrapper.get('older')}
                </ListSubheader>
              )
            }
          >
            {olderNotifications?.map((notification) => (
              <NotificationItem
                key={notification.timeStamp}
                notification={notification}
                markAsRead={() => handleItemAsRead(notification)}
                api={api}
              />
            ))}
          </List>
        </SimpleBar>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 48, // Above the "View All" button
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1,
              bgcolor: 'background.paper',
              p: 2,
              boxShadow: 1,
              minWidth: 400,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )}

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Box sx={{ p: 1, flexShrink: 0 }}>
          <Button
            fullWidth
            disableRipple
            color="inherit"
            onClick={() => {
              navigate(`/notifications`), handleClosePopover();
            }}
          >
            {ReactIntlWrapper.get('viewAll')}
          </Button>
        </Box>
      </Popover>
    </>
  );
}

// ----------------------------------------------------------------------

type NotificationItemProps = {
  notification: Notification;
  markAsRead?: () => Promise<boolean>;
  api: Api;
};

function NotificationItem({ notification, markAsRead, api }: NotificationItemProps) {
  let notificationParsed = notification;  
  if (typeof notification.message === 'string') {
    notificationParsed = { ...JSON.parse(notification?.message) };
  }

  const navigate = useNavigate();
  const rawParams = (notificationParsed as any).message?.Params || [];
  const resolvedParams = useResolvedParams(rawParams, api);
  const { renderedmessage } = renderMessage({ ...(notificationParsed as any).message, Params: resolvedParams }, notification.read);
  const priority = (notificationParsed as any).priority?.toLowerCase() || 'low';
  const rawAction = (notificationParsed as any).message?.Action;
  const action: string | undefined = typeof rawAction === 'string' ? rawAction.trim() || undefined : undefined;

  return (
    <ListItemButton
      sx={{
        py: 1,
        px: 2.5,
        gap: 1,
        mt: '1px',
        alignItems: 'flex-start',
      }}
    >
      {
        <ListItemAvatar sx={{ mt: 2, ml: -1 }}>
          <PriorityIcon priority={priority} />
        </ListItemAvatar>
      }
      <ListItemText
        primary={renderedmessage}
        sx={{ maxWidth: 'calc(100% - 120px)' }}
        secondary={
          <Typography
            variant="caption"
            sx={{
              mt: 0.5,
              gap: 0.5,
              display: 'flex',
              alignItems: 'center',
              color: 'text.disabled',
            }}
          >
            <Iconify width={14} icon="solar:clock-circle-outline" />
            {fToNow(new Date(notification?.timeStamp))}
          </Typography>
        }
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, mt: 1 }}>
        {action && (
          <Tooltip title={ReactIntlWrapper.get('viewDetails')} arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(action);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {Number(notification.read) == 0 && (
          <Tooltip title="Mark as read" arrow>
            <CircleTwoTone
              fontSize="small"
              onClick={(e) => {
                e.stopPropagation();
                markAsRead?.();
              }}
              sx={{
                color: 'green',
                '&.MuiSvgIcon-root': { fill: 'green' },
              }}
            />
          </Tooltip>
        )}
      </Box>
    </ListItemButton>
  );
}

// ----------------------------------------------------------------------

function renderMessage(message, read) {
  const renderedmessage = (
    <Typography
      variant="subtitle2"
      sx={{
        color: read == 0 ? 'text.primary' : 'text.secondary',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      }}
    >
      {' '}
      {getNotificationLocale(message.ResourceID, message.Params)}
    </Typography>
  );
  return {
    renderedmessage,
  };
}
